import { ObservationVariable } from '../../../shared/model/observation-variable.model';
import { ObservationVariableDTO } from '../../../shared/dataset/model/observation-units-search-request.model';
import { isNumeric } from '../../../shared/util/is-numeric';
import { ObservationUnitsSearchResponse } from '../../../shared/dataset/model/observation-units-search-response.model';
import { CellData, EditingDataMap } from '../../shared/study-inline-editor/study-inline-editor.component';
import { ObservationUnitData } from '../../../shared/dataset/model/observation-unit-data.model';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { DatasetModel } from '../../../shared/dataset/model/dataset.model';
import { VariableDetails } from '../../../shared/ontology/model/variable-details';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { StudyEditorContext } from '../../study-editor.context';
import { Principal } from '../../../shared';
import { VariableTypeEnum } from '../../../shared/ontology/variable-type.enum';
import { DataTypeEnum } from '../../../shared/ontology/data-type.enum';
import { Component, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ColumnData } from '../../shared/models/column-data.model';
import { ColumnDataService } from '../../shared/services/column-data.service';
import { COLUMN_SELECTOR_CHECKBOX_ID } from './column-selector/column-selector.component';
import { MAX_NAME_DISPLAY_SIZE } from '../../../app.constants';
import {
    STATIC_COLUMN_DESIGNATION, STATIC_COLUMN_ENTRY_CODE, STATIC_COLUMN_ENTRY_NO, STATIC_COLUMN_ENTRY_TYPE,
    STATIC_COLUMN_FEMALE_PARENT_GID, STATIC_COLUMN_FEMALE_PARENT_NAME, STATIC_COLUMN_FILES,
    STATIC_COLUMN_GID,
    STATIC_COLUMN_GROUPGID,
    STATIC_COLUMN_LINE_GID, STATIC_COLUMN_MALE_PARENT_GID, STATIC_COLUMN_MALE_PARENT_NAME, STATIC_COLUMN_OBS_UNIT_ID, STATIC_COLUMN_PLOT_NO, STATIC_COLUMN_SAMPLE_NAME,
    STATIC_COLUMN_SAMPLES, STATIC_COLUMN_TESTER_GID,
    STATIC_COLUMN_TRIAL_INSTANCE
} from '../../../shared/util/static-columns-util';

export enum CategoricalMatchByEnum {
    ID = 'id',
    NAME = 'name'
}

@Component({
    template: ''
})
export abstract class GenericDatasetComponent {

    public readonly MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    public readonly MISSING_TEXT = 'missing';

    public readonly CHECKBOX = COLUMN_SELECTOR_CHECKBOX_ID;
    public readonly STATIC_COLUMN_TRIAL_INSTANCE = STATIC_COLUMN_TRIAL_INSTANCE;
    public readonly STATIC_COLUMN_GID = STATIC_COLUMN_GID;
    public readonly STATIC_COLUMN_DESIGNATION = STATIC_COLUMN_DESIGNATION;
    public readonly STATIC_COLUMN_GROUPGID = STATIC_COLUMN_GROUPGID;
    public readonly STATIC_COLUMN_FILES = STATIC_COLUMN_FILES;
    public readonly STATIC_COLUMN_LINE_GID = STATIC_COLUMN_LINE_GID;
    public readonly STATIC_COLUMN_TESTER_GID = STATIC_COLUMN_TESTER_GID;
    public readonly STATIC_COLUMN_STOCK_ID = -1727;
    public readonly STATIC_COLUMN_OBS_UNIT_ID = STATIC_COLUMN_OBS_UNIT_ID;
    public readonly STATIC_COLUMN_ENTRY_NO = STATIC_COLUMN_ENTRY_NO;
    public readonly STATIC_COLUMN_PLOT_NO = STATIC_COLUMN_PLOT_NO;
    public readonly STATIC_COLUMN_ENTRY_TYPE = STATIC_COLUMN_ENTRY_TYPE;
    public readonly STATIC_COLUMN_SAMPLES = STATIC_COLUMN_SAMPLES;
    public readonly STATIC_COLUMN_FEMALE_PARENT_GID = STATIC_COLUMN_FEMALE_PARENT_GID;
    public readonly STATIC_COLUMN_FEMALE_PARENT_NAME = STATIC_COLUMN_FEMALE_PARENT_NAME;
    public readonly STATIC_COLUMN_MALE_PARENT_GID = STATIC_COLUMN_MALE_PARENT_GID;
    public readonly STATIC_COLUMN_MALE_PARENT_NAME = STATIC_COLUMN_MALE_PARENT_NAME;
    public readonly STATIC_COLUMN_ENTRY_CODE = STATIC_COLUMN_ENTRY_CODE;

    // Helper Methods
    readonly Math = Math;
    public readonly LOCATION_ID_TERM_ID = 8190;

    readonly VariableTypeEnum = VariableTypeEnum;
    readonly DataTypeEnum = DataTypeEnum;

    @ViewChild('p', { static: false }) columnFilterPopover: NgbPopover;

    items: ObservationUnitsSearchResponse[] = [];
    editingData: EditingDataMap<ObservationUnitData> = {};
    editingCell: CellData<ObservationUnitData | null>;

    lastClickIndex: any;
    isSelectAll: boolean = false;
    selectedItems: Set<number> = new Set<number>();
    numberOfItems = 0;
    numberOfFilteredItems = 0;
    isCategoricalDescriptionView = false;

    displayedColumns: ColumnData[] = [];

    currentPage = 1;
    pageSize = 50;

    activePopover?: NgbPopover;
    columnDataForFilter: ColumnData;

    currentDataset: DatasetModel;

    isReloadPending: boolean;
    reloadDelay: number = 800;
    reloadTimeout: any;

    isTableDataReloadPending: boolean;
    tableDataReloadTimeout: any;
    isPendingView: boolean = false;

    isDatasetReloadPending: boolean;
    datasetReloadTimeout: any;

    columnDataService: ColumnDataService;

    constructor(public studyEditorContext: StudyEditorContext,
                protected modalService: NgbModal,
                protected translateService: TranslateService,
                protected principal: Principal) {

    }

    protected transformObservationVariableToDTO(variables: ObservationVariable[]): ObservationVariableDTO[] {
        return variables.map((variable: ObservationVariable) => new ObservationVariableDTO(variable.termId, variable.name));
    }

    protected getCategoricalValueId(value: string, columnData: ColumnData): any | null {
        if (columnData.possibleValues && value !== 'missing') {
            let possibleValue = columnData.possibleValuesByName[value];
            if (possibleValue) {
                return possibleValue.id;
            } else {
                return null;
            }
        }
        return null;
    }

    protected validateDataOutOfRange(cellDataValue: string, columnData: ColumnData,  matchCategoricalBy: CategoricalMatchByEnum) {
        let invalid = false;

        let value = cellDataValue;
        let minVal = columnData.minRange;
        let maxVal = columnData.maxRange;

        if (minVal && maxVal) {
            invalid = this.validateNumericRange(minVal, maxVal, value, invalid);
        }
        invalid = this.validateCategoricalValues(columnData, cellDataValue, invalid, matchCategoricalBy);
        return invalid;
    }

    protected validateNumericRange(minVal: number, maxVal: number, value: string, invalid: boolean) {
        if (parseFloat(value) < (minVal) || parseFloat(value) > (maxVal)) {
            invalid = true;
        }
        return invalid;
    }

    protected validateCategoricalValues(columnData: ColumnData, cellDataValue: string, invalid: boolean, matchCategoricalBy: CategoricalMatchByEnum) {

        const possibleValuesMap = matchCategoricalBy === CategoricalMatchByEnum.ID ? columnData.possibleValuesById : columnData.possibleValuesByName;

        if (columnData.possibleValues
            && possibleValuesMap[cellDataValue] === undefined
            && cellDataValue !== 'missing') {

            invalid = true;
        }
        return invalid;
    }

    /**
     * Efficiently reload table
     */
    scheduleDatasetReload(): void {
        if (this.isDatasetReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.datasetReloadTimeout);
        } else {
            this.isDatasetReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.datasetReloadTimeout = setTimeout(() => {
            this.loadDataset(this.currentDataset.datasetId);
            this.isDatasetReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }


    /**
     * Efficiently reload table
     */
    scheduleReload(): void {
        if (this.isReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.reloadTimeout);
        } else {
            this.isReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.reloadTimeout = setTimeout(() => {
            this.loadTable();
            this.isReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }

    /**
     * Efficiently reload table data
     */
    scheduleTableDataReload(pageIndex, pageSize): void {
        if (this.isTableDataReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.tableDataReloadTimeout);
        } else {
            this.isTableDataReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.tableDataReloadTimeout = setTimeout(() => {
            this.loadTableData(pageIndex, pageSize);
            this.isTableDataReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }

    ngOnInit(): void {
        this.loadTable();
    }

    abstract loadDataset(datasetId: number): Promise<void>;

    abstract loadTable(): Promise<void>;

    abstract loadTableHeader(): Promise<void>;

    abstract loadTableData(pageIndex, pageSize): void;

    protected getSort() {
        const sort = this.displayedColumns.reduce((sort, column) => {
            if (column.sortingAsc !== null && column.sortingAsc !== undefined) {
                let sort: string = column.termId + '';
                if (column.variableTypeId === VariableTypeEnum.GERMPLASM_PASSPORT ||
                    column.variableTypeId === VariableTypeEnum.GERMPLASM_ATTRIBUTE) {
                    sort = 'VARIABLE_' + sort;
                } else if (column.variableTypeId === null && column.termId > 0) {
                    sort = 'NAME_' + sort;
                }
                return [`${sort},${(column.sortingAsc ? 'asc' : 'desc')}`];
            }
            return sort;
        }, []);

        // Default sort
        if (sort.length === 0) {
            return [`${this.STATIC_COLUMN_PLOT_NO},asc`];
        }

        return sort;
    }

    toggleSelect($event, index: number, item: ObservationUnitsSearchResponse, checkbox = false) {
        if ($event.ctrlKey || $event.metaKey) {
            $event.preventDefault();
        }

        if (!checkbox) {
            $event.preventDefault();
            $event.stopPropagation();
        }

        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.selectedItems.clear();
        }

        let items = [];
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1;
            const min = Math.min(this.lastClickIndex, index);
            items = this.items.slice(min, max);
        } else {
            items = [item];
            this.lastClickIndex = index;
        }

        const isClickedItemSelected = this.selectedItems.has(item.observationUnitId);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.observationUnitId);
            } else {
                this.selectedItems.add(item.observationUnitId);
            }
        }
    }

    isSelected(observationUnitId: number): boolean {
        return this.selectedItems.has(observationUnitId) || this.isSelectAll;
    }

    abstract getFilter(): any;

    isTextFilter(column: ColumnData): boolean {
        return false;
    }

    hasAnyAuthority(permissions: string[]): boolean {
        return this.principal.hasAnyAuthorityDirect(permissions);
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.scheduleTableDataReload(page, this.pageSize);
    }

    isCellDisabled(entry: ObservationUnitsSearchResponse, column: ColumnData) {
        if (this.isPendingView && this.isColumnEditable(column)) {
            const value = this.getCellValue(entry, column, 'draftValue');
            return (value === null || value === undefined);
        }
    }

    getCellClass(entry: ObservationUnitsSearchResponse, column: ColumnData, categoricalMatchBy: CategoricalMatchByEnum): string {

        const classes = ['entry-details'];

        if (this.isColumnEditable(column)) {
            classes.push('editable');
        }

        if (this.isPendingView && this.isColumnEditable(column)) {
            const draftValue = this.getCellValue(entry, column, 'draftValue');
            const invalid = this.validateDataOutOfRange(draftValue, column, categoricalMatchBy);
            if (invalid) {
                classes.push('invalid-value');
            }
        }

        if (!this.isPendingView && this.isColumnEditable(column)) {
            const value = this.getCellValue(entry, column, 'value');
            const invalid = this.validateDataOutOfRange(value, column, categoricalMatchBy);
            if (value && invalid) {
                classes.push('accepted-value');
            }
        }

        if (!this.isPendingView && this.isColumnEditable(column)) {
            const property = entry.variables[column.name];
            if (property) {
                if (property.status === 'MANUALLY_EDITED') {
                    classes.push('manually-edited-value');
                } else if (property.status === 'OUT_OF_SYNC') {
                    classes.push('out-of-sync-value');
                }
            }
        }

        return classes.join(' ');
    }

    abstract isColumnEditable(column: ColumnData): boolean;

    abstract concatAllColumns(): ColumnData[];

    abstract getCellValue(entry: ObservationUnitsSearchResponse, column: ColumnData, valueProperty: 'value' | 'draftValue'): string;

    applyColumnVisibilityFilter(): void {
        this.scheduleTableDataReload(this.currentPage - 1, this.pageSize);
    }

    getColumnClass(column: ColumnData) {
        let classNames: string[] = ['default'];
        if (this.isColumnEditable(column)) {
            classNames.push('entry-detail');
            if (column.formula) {
                classNames.push('derived-trait');
            } else {
                classNames.push('editable');
            }
        }
        return classNames.join(' ');
    }

    abstract getModel(column: ColumnData, entry: ObservationUnitsSearchResponse);

    selectEditableCell(entry: ObservationUnitsSearchResponse, row: number, col: number, $event?): void {

        const column = this.displayedColumns[col];
        const rowItem = this.items[row];
        const model = this.getModel(column, rowItem);

        if (!this.isColumnEditable(column)) {
            return;
        }

        if ($event) {
            $event.preventDefault();
            $event.stopPropagation();
        }

        // If the clicked cell is already editing, cancel the event.
        if (this.editingCell && this.editingCell.row === row && this.editingCell.col === col) {
            return;
        }

        this.clearSelected();

        if (this.editingCell) {
            this.editingCell.isEditing = false;
        }

        const cellData: CellData<ObservationUnitData> = {
            isEditing: true,
            model: model,
            row: row,
            col: col,
            previousValue: entry.variables[column.name] ? entry.variables[column.name].value : entry.environmentVariables[column.name].value,
            hasError: false
        };

        this.setCellData(row, col, cellData);
        this.editingCell = cellData;

    }

    abstract addVariable(variable: VariableDetails, variableTypeId: VariableTypeEnum): Promise<void>;

    abstract removeVariables(variables: ObservationVariable[], variableTypeId: VariableTypeEnum): Promise<void>;

    onPageSizeChange($event: Event) {
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    deselectAllColumns() {
        this.displayedColumns.forEach((column) => column.selected = false);
        this.scheduleReload();
    }

    selectAllColumns() {
        this.displayedColumns.forEach((column) => column.selected = true);
        this.scheduleReload();
    }

    areColumnsVisible(columns: ColumnData[]): boolean {
        return columns.filter((column) => column.visible).length > 0;
    }

    getCellData(row: number, col: number): CellData<ObservationUnitData> | null {
        try {
            // Input validation
            if (row < 0 || col < 0) {
                return null;
            }

            // Check if row exists
            const rowData = this.editingData[row];
            if (!rowData) {
                return null;
            }

            // Check if cell exists in row
            const cellData = rowData[col];
            if (!cellData) {
                return null;
            }

            return cellData;
        } catch (error) {
            console.error(`Error getting cell data for row ${row}, col ${col}:`, error);
            return null;
        }
    }

    // Helper method to set cell data
    setCellData(row: number, col: number, data: Partial<CellData<ObservationUnitData>>): void {
        if (!this.editingData[row]) {
            this.editingData[row] = {};
        }

        this.editingData[row][col] = {
            ...this.editingData[row][col],
            ...data,
            row,
            col
        };
    }


    // Helper method to check if cell exists
    hasCellData(row: number, col: number): boolean {
        return !!(this.editingData[row]?.[col]);
    }


    // Helper method to clear cell data
    clearCellData(row: number, col: number): void {
        if (this.editingData[row]) {
            delete this.editingData[row][col];

            // Clean up empty rows
            if (Object.keys(this.editingData[row]).length === 0) {
                delete this.editingData[row];
            }
        }
    }


    variableDataTypeIs(variable: ColumnData, type: DataTypeEnum): boolean {
        if (variable.variableType
            && (variable.variableTypeId === VariableTypeEnum.ENVIRONMENT_CONDITION || variable.variableTypeId === VariableTypeEnum.ENVIRONMENT_DETAIL)) {
            return variable.dataType && variable.dataType.valueOf() === DataTypeEnum[type];
        }
        return false;
    }

    onCellBlur(entry: ObservationUnitsSearchResponse, column: ColumnData, row: number, col: number) {
        this.updateObservation(entry, column);
        this.resetSelected();
    }

    resetSelected() {
        if (this.editingCell) {
            this.editingCell.isEditing = false;
            this.editingCell = null;
        }
    }

    onCellKeyDown(event: KeyboardEvent, entry: ObservationUnitsSearchResponse, column: ColumnData, row: number, col: number) {
        switch (event.key) {
        case 'Enter':
        case 'Escape':
            event.preventDefault();
            this.onCellBlur(entry, column, row, col);
            break;
        case 'Tab':
            event.preventDefault();
            this.onCellBlur(entry, column, row, col);
            const nextRow = row + (event.shiftKey ? -1 : 1);
            if (nextRow >= 0 && nextRow < this.items.length) {
                this.selectEditableCell(this.items[nextRow], nextRow, col);
            }
            break;
        case 'ArrowRight':
            event.preventDefault();
            // Find next editable column
            for (let nextCol = col + 1; nextCol < this.displayedColumns.length; nextCol++) {
                if (this.isColumnEditable(this.displayedColumns[nextCol]) && this.displayedColumns[nextCol].selected) {
                    this.onCellBlur(entry, column, row, col);
                    this.selectEditableCell(this.items[row], row, nextCol);
                    break;
                }
            }
            break;
        case 'ArrowLeft':
            event.preventDefault();
            // Find previous editable column
            for (let prevCol = col - 1; prevCol >= 0; prevCol--) {
                if (this.isColumnEditable(this.displayedColumns[prevCol]) && this.displayedColumns[prevCol].selected) {
                    this.onCellBlur(entry, column, row, col);
                    this.selectEditableCell(this.items[row], row, prevCol);
                    break;
                }
            }
            break;
        case 'ArrowUp':
            if (row > 0) {
                event.preventDefault();
                this.onCellBlur(entry, column, row, col);
                this.selectEditableCell(this.items[row - 1], row - 1, col);
            }
            break;
        case 'ArrowDown':
            if (row < this.items.length - 1) {
                event.preventDefault();
                this.onCellBlur(entry, column, row, col);
                this.selectEditableCell(this.items[row + 1], row + 1, col);
            }
            break;
        }
    }

    abstract updateObservation(entry: ObservationUnitsSearchResponse, column: ColumnData): Promise<void>;

    protected confirmOutOfBoundData(cellDataValue: string, columnData: ColumnData, matchCategoricalBy: CategoricalMatchByEnum): Promise<boolean> {
        if (cellDataValue) {
            const invalid = this.validateDataOutOfRange(cellDataValue, columnData, matchCategoricalBy);

            if (invalid) {
                // Open confirmation modal and return its promise
                const modalRef = this.modalService.open(ModalConfirmComponent);
                modalRef.componentInstance.title = this.translateService.instant('study-editor.study-dataset.confirm-out-of-bound.title');
                modalRef.componentInstance.message = this.translateService.instant('study-editor.study-dataset.confirm-out-of-bound.message');;
                modalRef.componentInstance.confirmLabel = this.translateService.instant('study-editor.study-dataset.confirm-out-of-bound.keep');;
                modalRef.componentInstance.cancelLabel = this.translateService.instant('study-editor.study-dataset.confirm-out-of-bound.discard');;
                modalRef.componentInstance.showButtonIcons = false;

                return modalRef.result
                    .then(() => true)
                    .catch(() => false);
            }
        }
        return Promise.resolve(true);

    }

    clearSelected() {
        this.selectedItems.clear();
        this.isSelectAll = false;
    }

    handleContextMenu(event: MouseEvent) {
        // Check if the right-click originated from an input or its label
        if (event.target instanceof HTMLInputElement ||
            event.target instanceof HTMLLabelElement ||
            (event.target instanceof HTMLElement && event.target.closest('input'))) {
            // Allow default context menu for inputs
            return;
        }

        // Prevent default context menu for the rest of the row
        event.preventDefault();

    }

    showColumnFilterPopover(column: ColumnData, p: NgbPopover) {
        this.columnDataForFilter = column;
        p.open({
            column: column
        });
    }

    sortColumn(column: ColumnData, ascending: boolean): void {

        // Clear sorting state for all displayed columns to ensure only one column can be sorted at a time
        this.resetSortingsOfDisplayedColumns();

        column.sortingAsc = ascending;
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    resetSortingsOfDisplayedColumns() {
        this.displayedColumns.forEach(column => {
            column.sortingAsc = null;
        });
    }

    filterSelectAll(column: ColumnData, isSelectAll: boolean) {
        if (column.possibleValues) {
            column.possibleValues.forEach(value => {
                value.isSelectedInFilters = isSelectAll;
            });
        }
    }

    filterSelectOption(isSelectedInFilters: any) {

    }

    filterByColumn(column: ColumnData) {
        this.clearSelected();
        column.isFiltered = true;
        this.columnFilterPopover.close();
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

    resetFilterByColumn(column: ColumnData) {
        this.clearSelected();
        column.isFiltered = false;
        column.query = '';
        column.sortingAsc = null;
        column.possibleValues?.forEach(value => {
            value.isSelectedInFilters = false;
        });
        column.isSelectAll = false;
        this.scheduleTableDataReload((this.currentPage - 1), this.pageSize);
    }

}
